<template>
  <v-card class="series-overview-card">
    <div class="series-overview-body px-8 pt-8 pb-4">
      <v-container>
        <v-row>
          <v-col cols="9" class="d-flex flex-column">
            <v-row>
              <v-col cols="12" class="pt-0">
                <div class="text-h6 font-weight-bold mb-4 text-secondary">
                  {{
                    report.release
                      ? new Date(report.release * 1000).getFullYear()
                      : 'N/A'
                  }}
                </div>
                <div class="text-h6 font-weight-bold text-secondary">
                  {{ report.title }}
                </div>
              </v-col>
            </v-row>
            <v-row class="mt-auto">
              <v-col cols="12" class="pb-0">
                <div
                  class="text-h6 font-weight-bold mb-2 text-secondary report-title"
                >
                  {{ gradeDisplay }}
                  Grade Report Overview
                </div>
                <router-link
                  class="text-primary"
                  :to="{
                    name: 'report-overview',
                    params: {
                      id: report.slug,
                    },
                  }"
                >
                  <v-icon icon="mdi-chevron-left" /> Back to Overview
                </router-link>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="3" align="right">
            <img
              :src="report.cover.uri"
              :alt="report.cover.alt"
              class="report-cover"
              @error="handleImageError"
            />
          </v-col>
        </v-row>
      </v-container>
    </div>
  </v-card>
</template>

<script setup lang="ts">
  import type { Report } from '../../store';
  import { computed } from 'vue';

  const props = defineProps<{
    report: Report;
    selectedGrade?: string;
  }>();

  function getOrdinalSuffix(n: number): string {
    const j = n % 10,
      k = n % 100;
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
  }

  const gradeWordToNumberMap: Record<string, number> = {
    first: 1,
    second: 2,
    third: 3,
    fourth: 4,
    fifth: 5,
    sixth: 6,
    seventh: 7,
    eighth: 8,
    ninth: 9,
    tenth: 10,
    eleventh: 11,
    twelfth: 12,
  };

  const gradeDisplay = computed(() => {
    if (!props.selectedGrade) return '';

    const gradeSlug = props.selectedGrade.toLowerCase();

    if (gradeSlug === 'kindergarten' || gradeSlug === 'k') {
      return 'Kindergarten';
    }

    const gradeWord = gradeSlug.replace(/-grade$/, '');

    if (gradeWordToNumberMap[gradeWord]) {
      const gradeNumber = gradeWordToNumberMap[gradeWord];
      return `${gradeNumber}${getOrdinalSuffix(gradeNumber)}`;
    }

    if (/^\d+$/.test(gradeWord)) {
      const gradeNumber = parseInt(gradeWord, 10);
      return `${gradeNumber}${getOrdinalSuffix(gradeNumber)}`;
    }

    return props.selectedGrade;
  });

  function handleImageError(event: Event) {
    const imgElement = event.target as HTMLImageElement;
    if (!imgElement) return;
    imgElement.src =
      'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAzMDAgNDAwIj48cmVjdCB3aWR0aD0iMzAwIiBoZWlnaHQ9IjQwMCIgZmlsbD0iI2ViZjdmYiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjQiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiMwMDdlYWIiPlJlcG9ydCBDb3ZlcjwvdGV4dD48L3N2Zz4=';
  }
</script>

<style lang="scss" scoped>
  .series-overview-card {
    .overview-info-block {
      .text-caption {
        text-transform: uppercase !important;
      }
      .text-subcaption {
        font-size: 16px;
        font-weight: bold;
        color: rgb(var(--v-theme-secondary));
        &.alt {
          font-size: 12px;
          font-weight: normal;
        }
      }
      &.gateway-status {
        .text-subcaption {
          font-size: 14px;
        }
      }
    }
    .series-overview-body {
      .report-cover {
        max-width: 232px;
      }
      .report-title {
        font-family: 'rockwell', sans-serif;
        font-size: 48px !important;
        font-weight: normal !important;
      }
    }
    .series-overview-footer {
      background-color: #f0f3f5;
      .v-col {
        .overview-info-block {
          width: 100%;
          border-right: 1px solid rgba(var(--v-theme-blue), 0.25);
        }
        &:last-child {
          .overview-info-block {
            border-right: none;
          }
        }
      }
    }
    &.v-card a.text-primary {
      font-size: 14px !important;
    }
  }
</style>
